import { InputAdornment } from '@mui/material';
import { FieldProps, getIn } from 'formik';
import { ChangeEvent, FocusEvent, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { IconButton } from '../../button/IconButton/IconButton';
import { Icon } from '../../icon/Icon';
import { FormHelperText } from '../../shared/components/FormHelperText/FormHelperText';
import { TextField, TextFieldProps } from '../TextField/TextField';
import { useMemoizedDataIds } from '../../shared/utils/dataId';
import styles from './FormikPasswordField.module.scss';
import { MaskedTextField } from '../MaskedTextField/MaskedTextField';
import { FORMIK_PASSWORD_FIELD_DATA_IDS } from './formikPasswordFieldDataId';

export type FormikPasswordFieldProps = TextFieldProps & {
  masked?: boolean;
  visibilityToggle?: boolean;
  onMaskResetCallback?: VoidFunction;
};

export function FormikPasswordField({
  field,
  form: { touched, errors, setFieldValue },
  type,
  label,
  id,
  masked,
  visibilityToggle = true,
  helperText = null,
  onBlur,
  onChange,
  onMaskResetCallback,
  placeholder,
  dataId,
  ...props
}: FormikPasswordFieldProps & FieldProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showMask, setShowMask] = useState(field.value?.length ? masked : false);

  const inputRef = useRef<HTMLInputElement>(null!);

  const dataIds = useMemoizedDataIds(
    FORMIK_PASSWORD_FIELD_DATA_IDS,
    `${dataId ? `${dataId}-` : ''}${field.name}`
  );

  const showError =
    (touched[field.name] && !!errors[field.name]) ||
    (getIn(errors, field.name) && getIn(touched, field.name));

  const resetMaskHandler = async () => {
    await setFieldValue(field.name, '');

    if (inputRef.current) {
      inputRef.current.focus();
    }

    setShowMask(false);

    if (onMaskResetCallback) {
      onMaskResetCallback();
    }
  };

  const onTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const onBlurEvent = (e: FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);

    if (onBlur) {
      onBlur(e);
    }
  };

  const onChangeEvent = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    field.onChange(e);

    if (onChange) {
      onChange(e);
    }
  };

  const textFieldCommonProps = {
    ...field,
    ...props,
    id: id || field.name,
    dataId: dataIds.textField,
    label,
    autoComplete: 'off',
    placeholder: placeholder || label ? String(label) : '',
    onBlur: onBlurEvent,
    onChange: onChangeEvent,
    error: !!showError,
    helperText: helperText
      ? FormHelperText({
          helperText,
          showError,
          fieldName: field.name,
          errorMessage: getIn(errors, field.name)
        })
      : null
  };

  return (
    <>
      {showMask ? (
        <MaskedTextField
          {...textFieldCommonProps}
          className={props.className}
          onChangeClick={resetMaskHandler}
        />
      ) : null}

      <TextField
        {...textFieldCommonProps}
        type={showPassword ? 'text' : 'password'}
        inputRef={inputRef}
        className={clsx(props.className, showMask && styles.inputFieldMasked)}
        InputProps={{
          ...props.InputProps,
          endAdornment:
            visibilityToggle && !props.disabled ? (
              <InputAdornment position='end' className='mr-2' data-id={dataIds.inputAdornment}>
                <IconButton
                  aria-label='toggle password visibility'
                  label={showPassword ? 'Hide Password' : 'Reveal Password'}
                  onClick={onTogglePasswordVisibility}
                  edge='start'
                  type='button'
                  dataId={dataIds.toggleButton}
                >
                  <Icon
                    dataId={dataIds.toggleIcon}
                    name={showPassword ? 'no-preview' : 'view-password'}
                  />
                </IconButton>
              </InputAdornment>
            ) : null
        }}
      />
    </>
  );
}
